import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Download, Share2 } from "lucide-react";

import { BrandLogo, ChampionCard, SponsorStrip } from "../components/tournament/Brand";
import { CREAM, INK } from "../components/tournament/PublicParts";
import { shareResults } from "../components/tournament/ShowcaseTab";
import NextTournamentSignup from "../components/tournament/NextTournamentSignup";
import { resultsPngUrl } from "../lib/tournamentApi";
import { usePublicTournament } from "../lib/usePublicTournament";
import { Stages } from "./PublicTournament";

/** Public results: champions per division + share graphics + final brackets. */
export default function PublicResults() {
  const { slug } = useParams();
  const { data, error } = usePublicTournament(slug);
  if (error && !data) return <div style={{ padding: 24, color: "#B22222" }} data-testid="results-error">{error}</div>;
  if (!data) return <div style={{ padding: 24, color: CREAM, background: INK, minHeight: "100vh" }}>Loading…</div>;
  const t = data.tournament;
  const teamsById = Object.fromEntries(data.teams.map((x) => [x.id, x]));
  return (
    <div data-testid="public-results" style={{ background: INK, minHeight: "100vh", color: CREAM, padding: "20px 18px 60px", fontFamily: "system-ui, sans-serif" }}>
      <style>{"@keyframes pt-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }"}</style>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <Link to={`/t/${slug}`} data-testid="results-back" style={{ color: CREAM, opacity: 0.7, fontSize: 13, display: "inline-flex", gap: 4, alignItems: "center", textDecoration: "none" }}>
          <ArrowLeft size={14} aria-hidden="true" /> Live hub</Link>
        <div style={{ display: "flex", gap: 14, alignItems: "center", margin: "14px 0 22px" }}>
          <BrandLogo branding={data.branding} size={64} />
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", color: "#4db87a", fontWeight: 800 }}>RESULTS</div>
            <h1 data-testid="results-title" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 44, margin: 0, lineHeight: 1 }}>{t.name}</h1>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {data.results.map((r) => (
            <DivisionResult key={r.division_id} r={r} data={data} slug={slug} teamsById={teamsById} />
          ))}
        </div>
        {t.tier !== "hub" && (
          <div data-testid="results-whiteboard-note" style={{ fontSize: 12, opacity: 0.6, marginTop: 12 }}>Champion and runner-up shown.</div>
        )}
        <NextTournamentSignup slug={slug} enabled={data.signup_enabled} />
        <div style={{ marginTop: 36 }}><SponsorStrip branding={data.branding} tile={48} /></div>
        <div style={{ marginTop: 28, fontSize: 11, opacity: 0.5, letterSpacing: "0.12em" }}>POWERED BY CHALKOS</div>
      </div>
    </div>
  );
}

function DivisionResult({ r, data, slug, teamsById }) {
  const [open, setOpen] = useState(false);
  const division = data.divisions.find((d) => d.id === r.division_id);
  const games = data.games.filter((g) => g.division_id === r.division_id);
  return (
    <div data-testid={`results-division-${r.division_id}`}>
      <ChampionCard result={r} testId={`results-card-${r.division_id}`} />
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {r.decided && (
          <>
            <button data-testid={`results-share-${r.division_id}`} onClick={() => shareResults(slug, { id: r.division_id, name: r.name }, data.tournament.name)} style={chip}>
              <Share2 size={14} aria-hidden="true" /> Share graphic</button>
            <a data-testid={`results-download-${r.division_id}`} href={resultsPngUrl(slug, r.division_id, "portrait", true)} style={chip}>
              <Download size={14} aria-hidden="true" /> Portrait</a>
            <a data-testid={`results-download-square-${r.division_id}`} href={resultsPngUrl(slug, r.division_id, "square", true)} style={chip}>
              <Download size={14} aria-hidden="true" /> Square</a>
          </>
        )}
        {games.length > 0 && (
          <button data-testid={`results-bracket-toggle-${r.division_id}`} onClick={() => setOpen(!open)} style={chip}>
            <ChevronDown size={14} aria-hidden="true" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
            {open ? "Hide bracket" : "Full bracket"}</button>
        )}
      </div>
      {open && <div style={{ marginTop: 12 }}><Stages division={division} games={games} teamsById={teamsById} pools={data.pools || []} /></div>}
    </div>
  );
}

const chip = { display: "inline-flex", gap: 6, alignItems: "center", background: "transparent", color: CREAM, cursor: "pointer",
               border: "0.5px solid rgba(245,240,232,0.3)", borderRadius: 999, padding: "7px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none" };
